import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, isBlockingError } from '../api/client';
import type { Agenda, Item } from '../types/agenda';

const DEBOUNCE_MS = 1500;

interface PendingItemOp {
  itemId: string;
  item: Item;
}

type PendingOp =
  | { kind: 'item'; op: PendingItemOp }
  | { kind: 'meta'; patch: Partial<Agenda> };

interface UseAutoSaveOptions {
  agendaId: string;
  serverVersionRef: React.MutableRefObject<number>;
  onItemSaved: (
    serverAgenda: Agenda,
    concurrentEditDetected: boolean,
    overwrotePrincipal?: string,
  ) => void;
  onMetaSaved: (serverAgenda: Agenda) => void;
  onSaveError: (message: string) => void;
  /**
   * Called when a save fails to persist (network/5xx/auth/unexpected 4xx). The
   * editor blocks further edits until the user reloads, so the user never keeps
   * editing against changes that aren't being saved.
   */
  onSaveBlocked: (error: unknown) => void;
}

export interface AutoSaveHandle {
  scheduleItemPatch: (itemId: string, item: Item) => void;
  scheduleMetaPatch: (patch: Partial<Agenda>) => void;
  cancelItemPatch: (itemId: string) => void;
  /**
   * Enqueue a structural mutation (add/remove/reorder, sentinel POST) onto the
   * session-wide mutation queue. The task runs only after every previously
   * enqueued send (item PATCHes included) has settled, so it can read a fresh
   * `serverVersionRef`/item baseline at execution time instead of a stale
   * synchronously-captured one. `isTail()` reports whether this is currently the
   * last enqueued mutation — callers use it for tail reconciliation (only the
   * tail replaces the draft wholesale; intermediate ops keep the optimistic
   * draft to avoid flicker).
   */
  enqueueMutation: <T>(task: (isTail: () => boolean) => Promise<T>) => Promise<T>;
  /** Fire all pending timers synchronously (for beforeunload). */
  flushAll: () => void;
  /**
   * Snapshot the server's `updatedAt` for each item. Call after any operation
   * that replaces local state from the server (initial load, reload, add/remove
   * /reorder responses) so the next item patch sends an accurate
   * `originalUpdatedAt` and avoids false concurrent-edit alarms.
   */
  recordServerItems: (items: Item[]) => void;
  /**
   * Snapshot of item edits that are still waiting in their debounce timer (i.e.
   * typed locally but not yet sent to the server), keyed by item id. Callers
   * that replace the draft wholesale from a server response must layer these
   * back on top, or an in-flight structural mutation's tail reconciliation will
   * clobber a title the user just typed but that hasn't been PATCHed yet.
   */
  getPendingItems: () => Map<string, Item>;
  pendingCount: number;
  isOnline: boolean;
}

export function useAutoSave({
  agendaId,
  serverVersionRef,
  onItemSaved,
  onMetaSaved,
  onSaveError,
  onSaveBlocked,
}: UseAutoSaveOptions): AutoSaveHandle {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);

  // Debounce timers keyed by itemId (or "__meta__")
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Pending data keyed by itemId (or "__meta__")
  const pendingItems = useRef<Map<string, PendingItemOp>>(new Map());
  const pendingMeta = useRef<Partial<Agenda> | null>(null);

  // Latest server-known `updatedAt` per item, refreshed on every successful
  // save and whenever fresh server state arrives (initial load, reload, etc.).
  // Read at SEND time (not schedule time) so back-to-back edits from this
  // client don't appear as concurrent edits against the client's own prior save.
  const lastSavedAt = useRef<Map<string, string>>(new Map());

  // Offline queue (in-memory; cleared on reconnect)
  const offlineQueue = useRef<PendingOp[]>([]);
  const drainingQueue = useRef(false);

  // Single session-wide mutation queue. Every agenda-mutating send — item
  // PATCHes, meta PATCHes, and structural add/remove/reorder ops — is chained
  // here so they execute strictly one at a time. Because every store commit
  // bumps the agenda version (item PATCHes included), the version is a single
  // global counter; per-op granularity is wrong. Serializing globally lets each
  // op read a fresh version/baseline at execution time and eliminates self-races
  // (structural-vs-structural and item-vs-structural alike).
  const mutationChain = useRef<Promise<void>>(Promise.resolve());
  // Monotonic sequence of the most recently enqueued mutation. A task is the
  // "tail" while no later mutation has been enqueued after it.
  const lastEnqueuedSeq = useRef(0);

  const enqueue = useCallback(
    <T,>(task: (isTail: () => boolean) => Promise<T>): Promise<T> => {
      const mySeq = ++lastEnqueuedSeq.current;
      const isTail = () => lastEnqueuedSeq.current === mySeq;
      const run = mutationChain.current.then(() => task(isTail));
      // Keep the chain alive even if a task rejects, so one failure doesn't
      // wedge every subsequent send.
      mutationChain.current = run.then(
        () => undefined,
        () => undefined,
      );
      return run;
    },
    [],
  );

  const getPendingItems = useCallback((): Map<string, Item> => {
    const m = new Map<string, Item>();
    for (const [id, op] of pendingItems.current) m.set(id, op.item);
    return m;
  }, []);

  const recordServerItems = useCallback((items: Item[]) => {
    for (const it of items) {
      if (it.updatedAt) lastSavedAt.current.set(it.id, it.updatedAt);
    }
  }, []);

  const recomputePending = useCallback(() => {
    const count =
      pendingItems.current.size +
      (pendingMeta.current ? 1 : 0) +
      offlineQueue.current.length;
    setPendingCount(count);
  }, []);

  // ── Execute a single item patch against the server ──────────────────────────

  const executeItemPatch = useCallback(
    (op: PendingItemOp): Promise<void> =>
      // Run on the session-wide queue so this patch never races another send
      // from this client. `originalUpdatedAt` is read inside the task — i.e.
      // after the prior send's response has updated `lastSavedAt` — so the
      // baseline is always fresh and writes apply in order. (A patch from
      // another tab/device still mismatches the baseline and is correctly
      // reported as a concurrent edit.)
      enqueue(async () => {
        const originalUpdatedAt = lastSavedAt.current.get(op.itemId);
        try {
          const resp = await api.patchItem(agendaId, op.itemId, op.item, originalUpdatedAt);
          serverVersionRef.current = resp.agenda.version;
          const serverItem = resp.agenda.items.find((i) => i.id === op.itemId);
          if (serverItem?.updatedAt) lastSavedAt.current.set(op.itemId, serverItem.updatedAt);
          onItemSaved(resp.agenda, resp.concurrentEditDetected, resp.overwrotePrincipal);
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) return; // item deleted by another editor
          if (isBlockingError(e)) {
            onSaveBlocked(e);
            return;
          }
          onSaveError(e instanceof Error ? e.message : 'Failed to save item');
        }
      }),
    [agendaId, enqueue, serverVersionRef, onItemSaved, onSaveError, onSaveBlocked],
  );

  // ── Execute a single meta patch against the server ─────────────────────────

  const executeMetaPatch = useCallback(
    (patch: Partial<Agenda>): Promise<void> =>
      // Meta PATCHes bump the agenda version too, so serialize them on the same
      // queue to keep `serverVersionRef` monotonic against structural ops.
      enqueue(async () => {
        try {
          const agenda = await api.patchAgendaMeta(agendaId, patch);
          serverVersionRef.current = agenda.version;
          onMetaSaved(agenda);
        } catch (e) {
          if (isBlockingError(e)) {
            onSaveBlocked(e);
            return;
          }
          onSaveError(e instanceof Error ? e.message : 'Failed to save metadata');
        }
      }),
    [agendaId, enqueue, serverVersionRef, onMetaSaved, onSaveError, onSaveBlocked],
  );

  // ── Drain the offline queue once back online ───────────────────────────────

  const drainQueue = useCallback(async () => {
    if (drainingQueue.current) return;
    drainingQueue.current = true;

    while (offlineQueue.current.length > 0) {
      if (!navigator.onLine) break;
      const pending = offlineQueue.current[0];
      if (pending.kind === 'item') {
        await executeItemPatch(pending.op);
      } else {
        await executeMetaPatch(pending.patch);
      }
      offlineQueue.current.shift();
      recomputePending();
    }

    drainingQueue.current = false;
  }, [executeItemPatch, executeMetaPatch, recomputePending]);

  // ── Online / offline event handlers ───────────────────────────────────────

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      void drainQueue();
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [drainQueue]);

  // ── Public API ────────────────────────────────────────────────────────────

  const scheduleItemPatch = useCallback(
    (itemId: string, item: Item) => {
      const existing = timers.current.get(itemId);
      if (existing) clearTimeout(existing);

      pendingItems.current.set(itemId, { itemId, item });
      recomputePending();

      const handle = setTimeout(() => {
        timers.current.delete(itemId);
        const op = pendingItems.current.get(itemId);
        if (!op) return;
        pendingItems.current.delete(itemId);
        recomputePending();

        if (!navigator.onLine) {
          offlineQueue.current.push({ kind: 'item', op });
          recomputePending();
        } else {
          void executeItemPatch(op);
        }
      }, DEBOUNCE_MS);

      timers.current.set(itemId, handle);
    },
    [executeItemPatch, recomputePending],
  );

  const scheduleMetaPatch = useCallback(
    (patch: Partial<Agenda>) => {
      const existing = timers.current.get('__meta__');
      if (existing) clearTimeout(existing);

      pendingMeta.current = { ...pendingMeta.current, ...patch };
      recomputePending();

      const handle = setTimeout(() => {
        timers.current.delete('__meta__');
        const p = pendingMeta.current;
        if (!p) return;
        pendingMeta.current = null;
        recomputePending();

        if (!navigator.onLine) {
          // Merge into any existing meta op in the offline queue.
          const idx = offlineQueue.current.findIndex((op) => op.kind === 'meta');
          if (idx >= 0) {
            offlineQueue.current[idx] = {
              kind: 'meta',
              patch: { ...(offlineQueue.current[idx] as { kind: 'meta'; patch: Partial<Agenda> }).patch, ...p },
            };
          } else {
            offlineQueue.current.push({ kind: 'meta', patch: p });
          }
          recomputePending();
        } else {
          void executeMetaPatch(p);
        }
      }, DEBOUNCE_MS);

      timers.current.set('__meta__', handle);
    },
    [executeMetaPatch, recomputePending],
  );

  const cancelItemPatch = useCallback(
    (itemId: string) => {
      const existing = timers.current.get(itemId);
      if (existing) clearTimeout(existing);
      timers.current.delete(itemId);
      pendingItems.current.delete(itemId);
      recomputePending();
    },
    [recomputePending],
  );

  const flushAll = useCallback(() => {
    timers.current.forEach((handle, key) => {
      clearTimeout(handle);
      timers.current.delete(key);
      if (key === '__meta__') {
        const p = pendingMeta.current;
        if (p) {
          pendingMeta.current = null;
          void executeMetaPatch(p);
        }
      } else {
        const op = pendingItems.current.get(key);
        if (op) {
          pendingItems.current.delete(key);
          void executeItemPatch(op);
        }
      }
    });
    recomputePending();
  }, [executeItemPatch, executeMetaPatch, recomputePending]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timers.current.forEach(clearTimeout);
    };
  }, []);

  return {
    scheduleItemPatch,
    scheduleMetaPatch,
    cancelItemPatch,
    enqueueMutation: enqueue,
    flushAll,
    recordServerItems,
    getPendingItems,
    pendingCount,
    isOnline,
  };
}
